import React, { useRef, useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { useDispatch, useSelector } from 'react-redux'
import get from 'lodash/get'
import { formatPrice } from '../../helpers/format'
import {
  incrementLineItemQuantity,
  decrementLineItemQuantity,
  removeFromCart
} from '../../redux/actions/cart'
import theme from '../../style/theme'
import { useHoverCursor } from '../Cursor'
import { getCartError } from '../../redux/slices/cart'

const LineItem = ({ lineItem }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const removeButtonRef = useRef()
  const incrementRef = useRef()
  const decrementRef = useRef()
  const [imageError, setImageError] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [localError, setLocalError] = useState(null)
  const [localQuantity, setLocalQuantity] = useState(0)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const updateTimeoutRef = useRef(null)
  const isMounted = useRef(true)

  useHoverCursor(removeButtonRef)
  useHoverCursor(incrementRef)
  useHoverCursor(decrementRef)

  if (!lineItem) return null

  const { variant, quantity } = lineItem
  const { product } = lineItem
  const variantTitle = get(variant, ['title'])
  const productTitle = get(product, ['title'])

  // console.log('LineItem › full object:', lineItem)
  // console.log('LineItem › variant.image:', variant.image)
  // console.log('LineItem › product.images:', product.images)
  // console.log('LineItem › computed price:', get(variant, ['price', 'amount']))
  // console.log('LineItem › sellingPlan:', lineItem.sellingPlan)

  // Ensure we're getting the price correctly
  const price = get(variant, ['price', 'amount'])

  // Get product handle for fallback image
  const productHandle = get(product, ['handle']) ||
                        (productTitle ? productTitle.toLowerCase().replace(/\s+/g, '-') : 'product')

  // Enhanced image path handling for server.js implementation
  const variantImageSrc = get(variant, ['image', 'src'])
  // console.log(`Variant image source: ${variantImageSrc}`)
  const variantImageUrl = get(variant, ['image', 'url'])
  // console.log(`Variant image URL: ${variantImageUrl}`)
  const productFirstImageUrl = get(product, ['images', 0, 'url'])
  const productFeaturedImageUrl = get(product, ['featuredImage', 'url'])

  // Create a list of possible image URLs to try
  const fallbackImageOptions = [
    // Primary image sources from API response
    variantImageSrc,
    variantImageUrl,
    productFirstImageUrl,
    productFeaturedImageUrl,

    // Shopify CDN URLs based on product handle
    `https://cdn.shopify.com/s/files/1/0552/6549/5309/products/${productHandle}.jpg`,
    `https://cdn.shopify.com/s/files/1/0552/6549/5309/products/${productHandle}.png`,
    `https://cdn.shopify.com/s/files/1/0552/6549/5309/products/${productHandle}_1.jpg`,

    // Generic placeholder as last resort
    'https://cdn.shopify.com/s/files/1/0552/6549/5309/files/product-placeholder.png',
    '/assets/product-placeholder.png'
  ].filter(Boolean) // Remove any null/undefined values

  // Get the current image URL based on the current index and falls back to subsequent options
  const imageToUse = !imageError ? (variantImageSrc || variantImageUrl || productFirstImageUrl || productFeaturedImageUrl) : fallbackImageOptions[currentImageIndex]
  const variantId = get(variant, ['id'])
  const lineItemId = lineItem.id // Use the actual line item ID for cart operations

  // console.log('LineItem › imageToUse:', imageToUse)

  // Update local quantity when prop changes
  useEffect(() => {
    setLocalQuantity(quantity)
  }, [quantity])

  useEffect(() => {
    // Set isMounted to false when component unmounts
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (localError) {
      const timer = setTimeout(() => setLocalError(null), 7000)
      return () => clearTimeout(timer)
    }
  }, [localError])

  const cartError = useSelector(getCartError)
  useEffect(() => {
    if (cartError && cartError.message?.includes(variantId)) {
      setLocalError(cartError.message)
      console.log('🚨 Showing cart error for this item:', cartError.message)
    }
  }, [cartError, variantId])

  // Handle quantity updates with debounce
  const handleQuantityChange = (newQuantity) => {
    if (isRemoving || isUpdating) return // Don't allow changes during operations

    setLocalQuantity(newQuantity)
    setIsUpdating(true)

    // Clear previous timeout if it exists
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }

    // Set new timeout for the update
    updateTimeoutRef.current = setTimeout(() => {
      if (newQuantity <= 0) {
        handleRemove()
      } else if (newQuantity > quantity) {
        dispatch(incrementLineItemQuantity(variantId))
          .catch((err) => {
            const msg = err?.message || 'Could not update quantity.'
            setLocalError(msg)
          })
      } else if (newQuantity < quantity) {
        dispatch(decrementLineItemQuantity(variantId))
      }

      // Clear updating flag after a short delay
      setTimeout(() => {
        if (isMounted.current) {
          setIsUpdating(false)
        }
      }, 500)
    }, 300) // Debounce time
  }

  const handleIncrement = () => {
    if (isRemoving || isUpdating) return // Don't allow changes during operations
    handleQuantityChange(localQuantity + 1)
  }

  const handleDecrement = () => {
    if (isRemoving || isUpdating) return // Don't allow changes during operations
    if (localQuantity > 1) {
      handleQuantityChange(localQuantity - 1)
    } else {
      handleRemove()
    }
  }

  const handleRemove = () => {
    if (isRemoving || isUpdating) return

    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }

    setIsRemoving(true)
    // console.log(`Removing item with line ID: ${lineItemId}`)

    // Use the actual line item ID for removal
    dispatch(removeFromCart(lineItemId))

    // Set a backup timeout to reset the loading state if the operation takes too long
    // This ensures the UI doesn't get stuck in a loading state
    setTimeout(() => {
      if (isMounted.current && isRemoving) {
        setIsRemoving(false)
        // console.log('Forcing reset of removal loading state after timeout')
      }
    }, 5000) // 5 second safety timeout
  }

  const handleImageError = () => {
    // console.log(`Image error for ${productTitle}, trying next fallback option`)

    // Try the next fallback image
    if (currentImageIndex < fallbackImageOptions.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
    }

    setImageError(true)
  }

  // Calculate total with local quantity to immediately show changes
  let lineItemTotal = price * localQuantity
  // console.log('LineItem › lineItemTotal:', lineItemTotal)

  // Make sure pricing exists before rendering
  if (!price || isNaN(parseFloat(price))) {
    try {
      const storedPrices = JSON.parse(window.localStorage.getItem('shopifyProductPrices') || '{}')
      const storedPrice = storedPrices[productHandle]
      if (storedPrice) {
        // We found a stored price, use it for display
        lineItemTotal = storedPrice * localQuantity
      }
    } catch (error) {
      console.error('Error retrieving stored price:', error)
    }
  }

  return (
    <div className={classes.lineItem}>
      {(isRemoving || isUpdating) && (
        <div className={classes.loadingOverlay}>
          <div className={classes.loader}> </div>
        </div>
      )}

      <div className={classes.imageContainer}>
        <img
          src={imageToUse}
          alt={productTitle}
          className={classes.image}
          onError={handleImageError}
        />
      </div>

      <div className={classes.content}>
        <div className={classes.titlePrice}>
          <h6 className={classes.title}>{productTitle}</h6>
          <span className={classes.price}>{formatPrice(lineItemTotal)}</span>
        </div>

        {variantTitle && variantTitle !== 'Default Title' && (
          <div className={classes.variant}>{variantTitle}</div>
        )}
        <div className={classes.purchasePlan}>
          {lineItem.sellingPlan ? (
            <div className={classes.subscriptionLabel}>
              {lineItem.sellingPlan.name}
            </div>
          ) : (
            <div className={classes.subscriptionLabel}>
              One-time purchase
            </div>
          )}
        </div>
        <div className={classes.controls}>
          <div className={classes.quantityControls}>
            <button
              onClick={handleDecrement}
              className={classes.quantityButton}
              aria-label='Decrease quantity'
              ref={decrementRef}
              disabled={isRemoving || isUpdating}
            >
              -
            </button>
            <input
              type='text'
              className={classes.quantityInput}
              value={isNaN(Number(localQuantity)) ? '0' : Number(localQuantity)}
              readOnly
            />
            <button
              onClick={handleIncrement}
              className={classes.quantityButton}
              aria-label='Increase quantity'
              ref={incrementRef}
              disabled={isRemoving || isUpdating}
            >
              +
            </button>
          </div>

          {localError && (
            <p style={{ color: '#d9534f', marginTop: '5px', fontSize: '12px' }}>{localError}</p>
          )}

          <button
            onClick={handleRemove}
            className={classes.removeButton}
            aria-label='Remove item'
            ref={removeButtonRef}
            disabled={isRemoving || isUpdating}
          >
            <svg fill='#000000' version='1.1' id='Capa_1' width='16px' height='16px' viewBox='0 0 408.483 408.483'>
              <g>
                <g>
                  <path d='M87.748,388.784c0.461,11.01,9.521,19.699,20.539,19.699h191.911c11.018,0,20.078-8.689,20.539-19.699l13.705-289.316
                    H74.043L87.748,388.784z M247.655,171.329c0-4.61,3.738-8.349,8.35-8.349h13.355c4.609,0,8.35,3.738,8.35,8.349v165.293
                    c0,4.611-3.738,8.349-8.35,8.349h-13.355c-4.61,0-8.35-3.736-8.35-8.349V171.329z M189.216,171.329
                    c0-4.61,3.738-8.349,8.349-8.349h13.355c4.609,0,8.349,3.738,8.349,8.349v165.293c0,4.611-3.737,8.349-8.349,8.349h-13.355
                    c-4.61,0-8.349-3.736-8.349-8.349V171.329L189.216,171.329z M130.775,171.329c0-4.61,3.738-8.349,8.349-8.349h13.356
                    c4.61,0,8.349,3.738,8.349,8.349v165.293c0,4.611-3.738,8.349-8.349,8.349h-13.356c-4.61,0-8.349-3.736-8.349-8.349V171.329z'
                  />

                  <path d='M343.567,21.043h-88.535V4.305c0-2.377-1.927-4.305-4.305-4.305h-92.971c-2.377,0-4.304,1.928-4.304,4.305v16.737H64.916
                    c-7.125,0-12.9,5.776-12.9,12.901V74.47h304.451V33.944C356.467,26.819,350.692,21.043,343.567,21.043z'
                  />
                </g>
              </g>
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  lineItem: {
    display: 'flex',
    padding: '16px 0',
    borderBottom: [1, 'solid', '#E5E5E5'],
    position: 'relative',
    '&:last-child': {
      borderBottom: 'none'
    }
  },

  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1
  },
  purchasePlan: {
    display: 'flex'
  },

  subscriptionLabel: {
    fontSize: '12px',
    marginTop: '4px',
    padding: '2px 12px',
    background: '#38524f1c',
    textAlign: 'center',
    borderRadius: '8px',
    marginBottom: '12px'
  },

  loader: {
    border: '3px solid rgba(56, 82, 79, 0.1)',
    borderRadius: '50%',
    borderTop: '3px solid #38524F',
    width: '25px',
    height: '25px',
    animation: '$spin 1s linear infinite'
  },

  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  },

  imageContainer: {
    flexShrink: 0,
    width: '70px',
    height: '70px',
    marginRight: '15px',
    background: '#f5f5f5',
    borderRadius: '4px',
    overflow: 'hidden'
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },

  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  titlePrice: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },

  title: {
    margin: 0,
    fontSize: '18px',
    fontFamily: theme.fonts.headings,
    fontWeight: 500,
    color: theme.colors.primary
  },

  price: {
    fontSize: '16px',
    fontFamily: theme.fonts.headings,
    fontWeight: 500,
    color: theme.colors.primary
  },

  variant: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '10px'
  },

  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto'
  },

  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    border: [1, 'solid', '#E5E5E5'],
    borderRadius: '4px',
    overflow: 'hidden'
  },

  quantityButton: {
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    color: theme.colors.primary,
    transition: 'background-color 0.2s ease',
    padding: 0,
    '&:hover': {
      backgroundColor: '#f5f5f5'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },

  quantityInput: {
    width: '30px',
    height: '30px',
    textAlign: 'center',
    border: 'none',
    fontSize: '14px',
    color: theme.colors.primary,
    padding: 0,
    backgroundColor: 'transparent'
  },

  removeButton: {
    background: 'transparent',
    border: 'none',
    color: theme.colors.primary,
    fontSize: '14px',
    textDecoration: 'underline',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    '&:hover': {
      opacity: 0.7
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  }
}, { name: 'LineItem' })

export default LineItem
